import { useEffect, useState } from 'react';
import LetterProgress from './components/LetterProgress';
import ReadingLevel from './components/ReadingLevel';
import ReportContainer from './components/ReportContainer';
import { useParams } from 'react-router-dom';
import MainLayout from 'components/layout/MainLayout';
import bitmap_background from 'assets/images/bitmap-background.png';
import { Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import useStudentProgressReportsStore from 'graph/state/query-hooks/use-student-progress-store';
import ActivityReport from './components/ActivityReport';
import WithQuery from 'components/layout/WithQuery';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import useAssignmentStore from 'graph/state/query-hooks/use-assignments-store';
import { FONT_GSS_FUTURA_BOLD } from '@monorepo/shared/src/themes/defaultTheme';
import {
  activityReportTitle,
  getCharacterReport,
  getStudentReport,
  mainLayoutHeaderProps,
  getCharacterIds,
} from '../utils/reports-utils';

const t = (key: string, options?) => translate('reports', key, options);

const renderLettersOrReadingLevel = (params, assignments) => {
  if (params.character) {
    return (
      <ReportContainer title={t('readingLevel')}>
        <ReadingLevel />
      </ReportContainer>
    );
  }
  return (
    assignments && (
      <ReportContainer title={t('letterProgress')}>
        <LetterProgress assignments={assignments} />
      </ReportContainer>
    )
  );
};

const ReportsPage: React.FC = () => {
  const classes = useStyles();
  const params = useParams();
  const [reportData, setReportData] = useState([] as any);
  const [characterIds, setCharacterIds] = useState([] as any);

  const [{ assignments, isLoading: assignmentsLoading, error: assignmentsError }] =
    useAssignmentStore();
  const [{ progressData, isLoading, error }] = useStudentProgressReportsStore(characterIds ?? []);

  useEffect(() => {
    const ids = assignments && getCharacterIds(assignments, params.character);
    setCharacterIds(ids);
  }, [assignments?.length]);

  useEffect(() => {
    if (!progressData) return;
    if (params.character) {
      const characterReport = getCharacterReport(progressData?.characterProgress?.[0]);
      setReportData(characterReport);
    } else {
      const studentReport = getStudentReport(progressData);
      setReportData(studentReport);
    }
  }, [progressData?.characterProgress?.length, params?.character]);
  return (
    <WithQuery loading={isLoading || assignmentsLoading} error={error || assignmentsError}>
      <MainLayout
        scrollY={true}
        backgroundImg={bitmap_background}
        menuProps={mainLayoutHeaderProps(!!params.character, true)}
      >
        {progressData?.characterProgress?.length ? (
          <Box className={classes.container}>
            <Box className={classes.studentName}>
              <Typography
                className={classes.subtitle}
              >{`${progressData?.student?.firstName} ${progressData?.student?.lastName}`}</Typography>
            </Box>
            <ReportContainer title={activityReportTitle(params)}>
              <ActivityReport reportData={reportData} />
            </ReportContainer>

            {renderLettersOrReadingLevel(params, assignments)}
          </Box>
        ) : (
          <Box className={classes.container}>
            <Box>
              <Typography className={classes.subtitle}>{t('noAssignments')}</Typography>
            </Box>
          </Box>
        )}
      </MainLayout>
    </WithQuery>
  );
};

export default ReportsPage;

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    maxWidth: 1024,
    display: 'flex',
    margin: '0 auto',
    height: '100%',
    alignItems: 'center',
    flexDirection: 'column',
  },
  subtitle: {
    fontSize: theme.typography.pxToRem(40),
    lineHeight: theme.typography.pxToRem(60),
    fontFamily: FONT_GSS_FUTURA_BOLD,
  },
  studentName: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
  },
}));
